import {
  DEFAULT_DEAL_FILTERS, pruneSelection, toggleSelection, type DealFilterOptions, type DealFilters, type FilterOption,
} from '@shared/flow/account-hierarchy-deal-model';
import { DEAL_STATUSES, type DealStatus } from '@shared/flow/account-hierarchy-deals';
import { FISCAL_PRESETS, type FiscalPreset } from '@shared/flow/fiscal-period';
import { MultiSelectField, SingleSelectField } from './SidebarFields';
import {
  DEAL_STATUS_FIELD_LABEL, FILTER_BAR_COPY, dealRangeLabel, dealStatusSelectionLabel, selectionLabel,
} from './hierarchyPresentation';

export interface DealsFiltersProps {
  filters: DealFilters;
  onChange: (patch: Partial<DealFilters>) => void;
  /** Pipeline / deal type options derived from the loaded deal buckets. */
  options: DealFilterOptions;
  now: Date;
}

function OptionField({ label, selection, options, onChange }: {
  label: string;
  selection: 'all' | string[];
  options: readonly FilterOption[];
  onChange: (next: 'all' | string[]) => void;
}) {
  // A retained selection may name options this hierarchy lacks: judge everything against the current ones.
  const effective = pruneSelection(selection, options);
  return (
    <MultiSelectField
      label={label}
      summary={selectionLabel(effective, options)}
      active={effective !== 'all'}
      options={options}
      isChecked={key => effective === 'all' || effective.includes(key)}
      onToggle={key => onChange(toggleSelection(selection, options, key))}
    />
  );
}

const STATUS_OPTIONS: FilterOption[] = DEAL_STATUSES.map(s => ({ key: s, label: DEAL_STATUS_FIELD_LABEL[s] }));

/** The Deals section: Status, Date range (fiscal, by close date), Pipeline and Deal type. */
export function DealsFilters({ filters, onChange, options, now }: DealsFiltersProps) {
  const toggleStatus = (status: DealStatus) => {
    const next = filters.statuses.includes(status)
      ? filters.statuses.filter(s => s !== status)
      : DEAL_STATUSES.filter(s => filters.statuses.includes(s) || s === status);
    // At least one status stays on: an empty selection would silently mean "everything but disqualified".
    if (next.length > 0) onChange({ statuses: next });
  };
  const statusChanged = [...filters.statuses].sort().join() !== [...DEFAULT_DEAL_FILTERS.statuses].sort().join();
  return (
    <>
      <MultiSelectField
        label={FILTER_BAR_COPY.statusLabel}
        summary={dealStatusSelectionLabel(filters.statuses)}
        active={statusChanged}
        options={STATUS_OPTIONS}
        isChecked={key => filters.statuses.some(s => s === key)}
        onToggle={key => { const status = DEAL_STATUSES.find(s => s === key); if (status) toggleStatus(status); }}
      />
      <SingleSelectField<FiscalPreset>
        label={FILTER_BAR_COPY.dateRangeLabel}
        hint={FILTER_BAR_COPY.dateRangeHint}
        value={filters.dateRange}
        items={FISCAL_PRESETS.map(p => ({ value: p, label: dealRangeLabel(p, now) }))}
        onChange={dateRange => onChange({ dateRange })}
      />
      <OptionField label={FILTER_BAR_COPY.pipelineLabel} selection={filters.pipelines} options={options.pipelines} onChange={pipelines => onChange({ pipelines })} />
      <OptionField label={FILTER_BAR_COPY.dealTypeLabel} selection={filters.dealTypes} options={options.dealTypes} onChange={dealTypes => onChange({ dealTypes })} />
    </>
  );
}
